import { ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route } from 'react-router-dom';
import { theme } from './assets/js/theme';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalDialog from './components/feedback/GlobalDialog';
import GlobalNotification from './components/feedback/GlobalNotification';
import AdminLayout from './components/layouts/AdminLayout';
import MainLayout from './components/layouts/MainLayout';
import { UserRole } from './constants/role';
import AppContextProvider from './context/AppContext';
import LineContextProvider from './context/LineContext';
import NotFoundPage from './pages/NotFoundPage';
import AddressAddPage from './pages/address/AddressAddPage';
import AddressEditPage from './pages/address/AddressEditPage';
import AdressListPage from './pages/address/AddressListPage';
import AdminAddCompanyPage from './pages/admin/AdminAddCompanyPage';
import AdminCompanyDetailPage from './pages/admin/AdminCompanyDetailPage';
import AdminImportCompanyPage from './pages/admin/AdminImportCompanyPage';
import AdminImportPointPage from './pages/admin/AdminImportPointPage';
import AdminListPage from './pages/admin/AdminListPage';
import AdminRewardDetailPage from './pages/admin/AdminRewardDetailPage';
import CartPage from './pages/cart/CartPage';
import EditCompanyPage from './pages/company/EditCompanyPage';
import HistoryPage from './pages/history/HistoryPage';
import HomePage from './pages/homepage/HomePage';
import MemberPage from './pages/member/MemberPage';
import MyCouponDetailPage from './pages/my-coupon/MyCouponDetailPage';
import MyCouponPage from './pages/my-coupon/MyCouponPage';
import PrivilegePage from './pages/privilege/PrivilegePage';
import RedirectPage from './pages/redirect';
import RegisterPage from './pages/register/RegisterPage';
import RewardDetailPage from './pages/reward/RewardDetailPage';
import RewardPage from './pages/reward/RewardPage';

const App = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LineContextProvider>
          <AppContextProvider>
            <HelmetProvider>
              <ThemeProvider theme={theme}>
                <ConfigProvider
                  theme={{
                    components: {
                      Table: {
                        fontSize: '1.25rem',
                        headerBg: '#005972',
                        headerColor: '#ffffff',
                        headerSortHoverBg: '#004B61',
                        headerSortActiveBg: '#004B61',
                        headerSplitColor: '#ffffff',
                        colorPrimary: '#ffffff',
                        borderColor: '#e5e5e5',
                        lineHeight: '1.25',
                      },
                      Pagination: {
                        fontSize: '1.25rem',
                        colorPrimary: '#004B61',
                        colorText: '#505050',
                        itemSize: 36,
                      },
                      Select: {
                        fontSize: '1.25rem',
                        controlHeight: 36,
                      },
                    },
                  }}
                >
                  <Routes>
                    <Route path="/" element={<RedirectPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/register/sale" element={<RegisterPage />} />
                    <Route path="/home" element={<HomePage />} />

                    <Route element={<MainLayout />}>
                      <Route path="/member" element={<MemberPage />} />
                      <Route path="/privilege" element={<PrivilegePage />} />
                      <Route path="/history" element={<HistoryPage />} />
                      <Route path="/reward" element={<RewardPage />} />
                      <Route path="/reward/:rewardId" element={<RewardDetailPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/address" element={<AdressListPage />} />
                      <Route path="/address/add" element={<AddressAddPage />} />
                      <Route path="/address/edit/:addressId" element={<AddressEditPage />} />
                      <Route path="/my-coupon" element={<MyCouponPage />} />
                      <Route path="/my-coupon/:code" element={<MyCouponDetailPage />} />
                    </Route>

                    <Route
                      element={
                        <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="/admin" element={<AdminListPage />} />
                      <Route path="/admin/:id" element={<AdminCompanyDetailPage />} />
                      <Route path="/admin/reward/:id" element={<AdminRewardDetailPage />} />
                      <Route path="/admin/company/add" element={<AdminAddCompanyPage />} />
                      <Route path="/admin/company/:id" element={<EditCompanyPage />} />
                      <Route path="/admin/import" element={<AdminImportCompanyPage />} />
                      <Route path="/admin/import-point" element={<AdminImportPointPage />} />
                    </Route>

                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>

                  <GlobalDialog />
                  <GlobalNotification />
                </ConfigProvider>
              </ThemeProvider>
            </HelmetProvider>
          </AppContextProvider>
        </LineContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
